import { SupplierMessage } from '../../../../types/SupplierMessage';
import { AsyncComboBox, AsyncComboBoxProps, ComboBoxOption } from '../../../cdl/ComboBox/ComboBox';
import { getAssignmentList } from '../../api/clients/gateway.api';
import { queries } from '../../api/queryKeys/queries';
import { formatCompanyName } from '../../helpers/formatCompanyName.helper';

export interface AssignedSupplierSelectProps
    extends Omit<
        AsyncComboBoxProps<ComboBoxOption>,
        'value' | 'onChange' | 'optionsQueryKey' | 'optionsQueryFn' | 'selectedOptionQueryKey' | 'selectedOptionQueryFn'
    > {
    customerIds: string[];
    onChange: (supplierId: string) => void;
    value: string;
}

export const AssignedSupplierSelect = ({ customerIds, value, onChange, ...rest }: AssignedSupplierSelectProps) => {
    const filterAssignedSuppliers = (options: ComboBoxOption[], search: string) =>
        options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));

    const loadAssignedSuppliers = async (supplierIds?: string[]): Promise<ComboBoxOption[]> => {
        if (!customerIds.length) {
            return [];
        }

        const assignments = await getAssignmentList({ customerIds, supplierIds });
        const uniqueSuppliers = new Map<string, ComboBoxOption>();

        assignments
            .map((assignment) => assignment.supplier)
            .filter((supplier?: SupplierMessage) => supplier !== undefined)
            .forEach((supplier: SupplierMessage) => {
                uniqueSuppliers.set(supplier.id, {
                    value: supplier.id,
                    label: formatCompanyName({
                        company: supplier,
                    })!,
                });
            });

        return Array.from(uniqueSuppliers.values());
    };

    const loadAssignedSupplier = async (supplierId: string): Promise<ComboBoxOption | null> => {
        const options = await loadAssignedSuppliers([supplierId]);

        return options.find((option) => option.value === supplierId) ?? null;
    };

    return (
        <AsyncComboBox
            {...rest}
            value={value || null}
            onChange={(selectedValue) => onChange(selectedValue ?? '')}
            optionsQueryKey={() => queries.assignments.comboBoxOptions({ customerIds }).queryKey}
            optionsQueryFn={() => loadAssignedSuppliers()}
            customOptionsFilter={filterAssignedSuppliers}
            selectedOptionQueryKey={(selectedValue) =>
                queries.assignments.comboBoxOptions({ customerIds, supplierIds: [selectedValue] }).queryKey
            }
            selectedOptionQueryFn={loadAssignedSupplier}
        />
    );
};
